import _ from "lodash";
import { flow, types } from "mobx-state-tree";
import { getEnv } from "~/mobx/utils/getEnv";
import { Clinic } from "./Clinic";
import type { ClinicInstance, ClinicSnapshotIn } from "./Clinic";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface ClinicStoreInstance extends Instance<typeof ClinicStore> {}
export interface ClinicStoreSnapshotIn extends SnapshotIn<typeof ClinicStore> {}
export interface ClinicStoreSnapshotOut
  extends SnapshotOut<typeof ClinicStore> {}

export const ClinicStore = types
  .model("ClinicStore", {
    map: types.map(Clinic),
    shouldShowSearch: types.boolean,
  })
  .actions((self) => {
    return {
      toggleSearch() {
        self.shouldShowSearch = !self.shouldShowSearch;
      },
      processOne(data: ClinicSnapshotIn) {
        const existing = self.map.get(data.id);
        if (existing) {
          _.mergeWith(existing, data, (_, next) => {
            if (Array.isArray(next)) return next; // Treat arrays like atoms
          });
          return existing;
        }
        return self.map.put(data as ClinicSnapshotIn);
      },
    };
  })
  .actions((self) => {
    return {
      processMany(dataList: ClinicSnapshotIn[]) {
        return dataList.map((data) => {
          return self.processOne(data);
        });
      },
    };
  })
  .actions((self) => {
    return {
      getClinicList: flow<
        ClinicInstance[],
        [string, number | undefined, number | undefined]
      >(function* getClinicList(
        sort: string,
        latitude?: number,
        longitude?: number,
      ): any {
        const env = getEnv(self);

        const uri =
          sort === "geocode"
            ? `clinics?sort=${sort}&latitude=${latitude}&longitude=${longitude}`
            : "clinics";

        const response: AxiosResponse = yield env.baseAuthHttp.get(uri);

        const clinics = response.data.data;

        response.data = self.processMany(clinics);

        return response.data;
      }),
      getClinic: flow<ClinicInstance, [string]>(function* getClinic(
        id: string,
      ): any {
        const env = getEnv(self);

        const response: AxiosResponse = yield env.baseHttp.get(`clinics/${id}`);

        const clinic = response.data.data;

        response.data = self.processOne(clinic);

        return response.data;
      }),

      getRecommendedClinicList: flow<ClinicInstance[], []>(
        function* getRecommendedClinicList(): any {
          const env = getEnv(self);

          const response: AxiosResponse = yield env.baseAuthHttp.get(
            "clients/me/recommended-clinics",
          );

          const clinics = response.data.data;

          response.data = self.processMany(clinics);

          return response.data;
        },
      ),
    };
  });
